/**
 * @version 1.0
 * @author Kelnik Studios {http://kelnik.ru}
 * @link https://gitbook.kelnik.ru/main/front-end/components/popup.html#facade documentation
 */

/**
 * DEPENDENCIES
 */
import {
    AjaxPopup,
    FullPopup,
    GalleryPopup,
    JsonPopup,
    OnlinePopup,
    ProgressPopup,
    StaticPopup,
    StaticPopupWithHash,
    StaticPopupWithImage,
    VideoPopup,
    ZoomPopup
} from './popup';
import {initVideoTabs} from '../../common/scripts/common';

export default class PopupFacade {
    /* eslint-disable max-lines-per-function */
    constructor(options = {}) {
        this.openBtns = options.openButtons;
        this.popupsGroups = {
            href: {
                Construct: StaticPopupWithHash,
                popups   : {},
                options  : {
                    modify     : options.modify,
                    removeDelay: 300
                }
            },
            hrefNoHash: {
                Construct: StaticPopup,
                popups   : {},
                options  : {
                    removeDelay: options.removeDelay
                }
            },
            ajax: {
                Construct: AjaxPopup,
                popups   : {},
                options  : {
                    removeDelay: options.removeDelay
                }
            },
            json: {
                Construct: JsonPopup,
                popups   : {},
                options  : {
                    modify     : options.modify,
                    removeDelay: options.removeDelay
                }
            },
            galleryPopup: {
                Construct: GalleryPopup,
                popups   : {},
                options  : {
                    modify     : 'popup_theme_white',
                    removeDelay: 300
                }
            },
            progressPopup: {
                Construct: ProgressPopup,
                popups   : {},
                options  : {
                    modify     : 'popup_theme_white',
                    removeDelay: 300
                }
            },
            callback: {
                Construct: StaticPopupWithHash,
                popups   : {},
                options  : {
                    removeDelay         : options.removeDelay,
                    closeButtonAriaLabel: options.closeButtonAriaLabel,
                    modify              : options.modify
                }
            },
            full: {
                Construct: FullPopup,
                popups   : {},
                options  : {
                    name                : 'full',
                    removeDelay         : options.removeDelay,
                    closeButtonAriaLabel: options.closeButtonAriaLabel
                }
            },
            image: {
                Construct: StaticPopupWithImage,
                popups   : {},
                options  : {
                    modify     : options.modify,
                    removeDelay: options.removeDelay
                }
            },
            online: {
                Construct: OnlinePopup,
                popups   : {},
                options  : {
                    modify     : 'popup_theme_white',
                    removeDelay: 300,
                    onCreate   : () => {
                        initVideoTabs('.j-popup__content');
                    }
                }
            },
            video: {
                Construct: VideoPopup,
                popups   : {},
                options  : {
                    modify     : options.modify,
                    removeDelay: options.removeDelay
                }
            },
            zoom: {
                Construct: ZoomPopup,
                popups   : {},
                options  : {
                    modify     : options.modify,
                    removeDelay: options.removeDelay
                }
            },
            Cookies: {
                Construct: StaticPopup,
                popups   : {},
                options  : {
                    removeDelay: options.removeDelay
                }
            }
        };

        this._checkOpenButtonsExistence();
    }
    /* eslint-enable max-lines-per-function */

    /**
     * @Public
     * Входная точка проекта
     */
    init() {
        this._groupPopupsByType();
        this._initPopups();
    }

    /**
     * @Private
     * Проверка на передачу наличие входнх данных
     */
    _checkOpenButtonsExistence() {
        if (!this.openBtns.length) {
            throw new Error('There is no PopupFacade open buttons in constructor');
        }
    }

    /**
     * @Private
     * Группировка кнопок по названиям ссылок внутри групп
     * @param {Node} button - кнопка для открытия попапа
     * @param {String} group - название группы, к которой относится попап
     * @param {String} link - ссылка на контент попапа
     */
    _addToGroup(button, group, link) {
        if (!this.popupsGroups[group] || !this.popupsGroups[group].popups[button.dataset[link]]) {
            this.popupsGroups[group].popups[button.dataset[link]] = [];
        }

        this.popupsGroups[group].popups[button.dataset[link]].push(button);
    }

    /**
     * @Private
     * Группировка кнопок по дата атрибутам
     *
     */
    _groupPopupsByType() {
        this.openBtns.forEach((button) => {
            if (button.hasAttribute('data-callback')) {
                this._addToGroup(button, 'callback', 'href');
            } else if (button.hasAttribute('data-href') && button.hasAttribute('data-no-hash')) {
                this._addToGroup(button, 'hrefNoHash', 'href');
            } else if (button.hasAttribute('data-href') && button.hasAttribute('data-video')) {
                this._addToGroup(button, 'video', 'href');
            } else if (button.hasAttribute('data-href') && button.hasAttribute('data-zoom')) {
                this._addToGroup(button, 'zoom', 'zoom');
            } else if (button.hasAttribute('data-href')) {
                this._addToGroup(button, 'href', 'href');
            } else if (button.hasAttribute('data-ajax-online')) {
                this._addToGroup(button, 'online', 'ajax');
            } else if (button.hasAttribute('data-progress')) {
                this._addToGroup(button, 'progressPopup', 'href');
            } else if (button.hasAttribute('data-ajax')) {
                this._addToGroup(button, 'ajax', 'ajax');
            } else if (button.hasAttribute('data-json')) {
                this._addToGroup(button, 'json', 'json');
            } else if (button.hasAttribute('data-gallery')) {
                this._addToGroup(button, 'galleryPopup', 'slider');
            } else if (button.hasAttribute('data-full')) {
                this._addToGroup(button, 'full', 'full');
            } else if (button.hasAttribute('data-image')) {
                this._addToGroup(button, 'image', 'image');
            }
        });
    }

    /**
     * @Private
     * Инициализация нужных попапов в зависимости от групп и ссылок кнопок
     *
     */
    _initPopups() {
        for (const group in this.popupsGroups) {
            if (Object.prototype.hasOwnProperty.call(this.popupsGroups, group)) {
                for (const popup in this.popupsGroups[group].popups) {
                    if (Object.prototype.hasOwnProperty.call(this.popupsGroups[group].popups, popup)) {
                        Object.assign(this.popupsGroups[group].options, {
                            openButtons: this.popupsGroups[group].popups[popup]
                        });

                        new this.popupsGroups[group].Construct(this.popupsGroups[group].options).init();
                    }
                }
            }
        }
    }
}
